import { useDataEngine } from "@dhis2/app-runtime";

export const CONFIG = {
  program: "RSANdetbnWC",
  trackedEntityType: "OaAbpIs1cV7",
  stages: {
    config: "RF0WnJmJczX",        // Leadership Competency Configuration (master list)
    weights: "zY4mj9a30tk",       // Weight Targets (deprecated - use config stage)
    midYear: "mJ3mC8f2eMv",       // Mid-Year Review
    endYear: "TiLy9LFPaNo",       // Year-End Review
    approvals: "zC1XdAhrAhr",     // Approvals
  },
  de: {
    // Competency config data elements (for RF0WnJmJczX stage)
    competencyId: "hM7qEqQBciM",        // Leadership Competency ID
    competencyCode: "KekX9kvB4b2",      // Competency Code (name)
    competencyDefinition: "n9DVY0rbIsl", // Competency Definition
    weightValue: "NnO0P1bqPGw",         // WEIGHT VALUE
    
    // Rating data elements (for mJ3mC8f2eMv and TiLy9LFPaNo stages)
    ratingScore: "fPIRWdClNPe",         // RATING SCORE
    ratingComment: "SVFdFerDLV1",       // RATING COMMENT
    
    // Approval data elements (for zC1XdAhrAhr stage)
    approvalStatus: "iNiXoYwyNUL",      // Leadership Approval Status
    approvalRemark: "s7aNZvcg8pH",      // Leadership Approval Remark
    
    // Common references
    positionReference: "SlgO60cMK3F"
  },
};

export function useLeadershipServices() {
  const engine = useDataEngine();

  // --- CREATE Event ---
  const createEvent = async (eventData) => {
    const query = {
      resource: "events",
      type: "create",
      data: eventData,
    };
    return await engine.mutate(query);
  };

  // --- READ Events ---
  const getEvents = async ({ program, trackedEntityInstance, orgUnit, programStage }) => {
    const params = {
      program,
      trackedEntityInstance,
      orgUnit,
      fields: "event,programStage,eventDate,dataValues[dataElement,value]",
      pageSize: 200,
    };
    if (programStage) {
      params.programStage = programStage;
    }
    const query = {
      events: {
        resource: "events",
        params: params,
      },
    };
    const result = await engine.query(query);
    return result.events?.events || [];
  };

  // --- UPDATE Event ---
  const updateEvent = async (eventId, updatedData) => {
    const query = {
      resource: `events/${eventId}`,
      type: "update",
      data: updatedData,
    };
    return await engine.mutate(query);
  };

  // --- DELETE Event ---
  const deleteEvent = async (eventId) => {
    const query = {
      resource: `events/${eventId}`,
      type: "delete",
    };
    return await engine.mutate(query);
  };

  // --- Create Competency Config Event (master record) ---
  const createCompetencyConfig = async (teiId, orgUnitId, enrollmentId, competency) => {
    const dataValues = [
      { dataElement: CONFIG.de.competencyId, value: competency.competencyId || `${Date.now()}` },
      { dataElement: CONFIG.de.competencyCode, value: competency.name },
      { dataElement: CONFIG.de.competencyDefinition, value: competency.definition || competency.name },
    ];
    
    if (competency.weight !== null && competency.weight !== undefined) {
      dataValues.push({ dataElement: CONFIG.de.weightValue, value: competency.weight.toString() });
    }
    
    return createEvent({
      program: CONFIG.program,
      programStage: CONFIG.stages.config,
      trackedEntityInstance: teiId,
      orgUnit: orgUnitId,
      enrollment: enrollmentId,
      eventDate: new Date().toISOString().split('T')[0],
      status: "COMPLETED",
      dataValues: dataValues,
    });
  };

  // --- Update Competency Config Event ---
  const updateCompetencyConfig = async (eventId, teiId, orgUnitId, enrollmentId, competency) => {
    const dataValues = [
      { dataElement: CONFIG.de.competencyId, value: competency.competencyId },
      { dataElement: CONFIG.de.competencyCode, value: competency.name },
      { dataElement: CONFIG.de.competencyDefinition, value: competency.definition || competency.name },
    ];
    
    if (competency.weight !== null && competency.weight !== undefined) {
      dataValues.push({ dataElement: CONFIG.de.weightValue, value: competency.weight.toString() });
    }
    
    return updateEvent(eventId, {
      event: eventId,
      program: CONFIG.program,
      programStage: CONFIG.stages.config,
      trackedEntityInstance: teiId,
      orgUnit: orgUnitId,
      enrollment: enrollmentId,
      eventDate: new Date().toISOString().split('T')[0],
      status: "COMPLETED",
      dataValues: dataValues,
    });
  };

  // --- Save Rating for Competency ---
  const saveRating = async (teiId, orgUnitId, enrollmentId, competencyId, ratingIndex, ratingValue, comment) => {
    const programStage = ratingIndex === 0 ? CONFIG.stages.midYear : CONFIG.stages.endYear;
    
    // First, check if a rating event already exists for this competency and period
    const existingEvents = await getEvents({
      program: CONFIG.program,
      trackedEntityInstance: teiId,
      orgUnit: orgUnitId,
      programStage: programStage,
    });
    
    const existingEvent = existingEvents.find(event => {
      const idDV = event.dataValues?.find(dv => dv.dataElement === CONFIG.de.competencyId);
      return idDV?.value === competencyId;
    });
    
    const dataValues = [
      { dataElement: CONFIG.de.competencyId, value: competencyId },
      { dataElement: CONFIG.de.ratingScore, value: ratingValue.toString() },
      { dataElement: CONFIG.de.ratingComment, value: comment || "" },
    ];
    
    if (existingEvent) {
      // Update existing event
      return updateEvent(existingEvent.event, {
        event: existingEvent.event,
        program: CONFIG.program,
        programStage: programStage,
        trackedEntityInstance: teiId,
        orgUnit: orgUnitId,
        enrollment: enrollmentId,
        eventDate: new Date().toISOString().split('T')[0],
        status: "COMPLETED",
        dataValues: dataValues,
      });
    } else {
      // Create new event
      return createEvent({
        program: CONFIG.program,
        programStage: programStage,
        trackedEntityInstance: teiId,
        orgUnit: orgUnitId,
        enrollment: enrollmentId,
        eventDate: new Date().toISOString().split('T')[0],
        status: "COMPLETED",
        dataValues: dataValues,
      });
    }
  };

  // --- Save Approval for Competency ---
  const saveApproval = async (teiId, orgUnitId, enrollmentId, competencyId, status, remark) => {
    // Check if approval event already exists
    const existingEvents = await getEvents({
      program: CONFIG.program,
      trackedEntityInstance: teiId,
      orgUnit: orgUnitId,
      programStage: CONFIG.stages.approvals,
    });
    
    const existingEvent = existingEvents.find(event => {
      const idDV = event.dataValues?.find(dv => dv.dataElement === CONFIG.de.competencyId);
      return idDV?.value === competencyId;
    });
    
    const dataValues = [
      { dataElement: CONFIG.de.competencyId, value: competencyId },
      { dataElement: CONFIG.de.approvalStatus, value: status },
      { dataElement: CONFIG.de.approvalRemark, value: remark || "" },
    ];
    
    if (existingEvent) {
      return updateEvent(existingEvent.event, {
        event: existingEvent.event,
        program: CONFIG.program,
        programStage: CONFIG.stages.approvals,
        trackedEntityInstance: teiId,
        orgUnit: orgUnitId,
        enrollment: enrollmentId,
        eventDate: new Date().toISOString().split('T')[0],
        status: "COMPLETED",
        dataValues: dataValues,
      });
    } else {
      return createEvent({
        program: CONFIG.program,
        programStage: CONFIG.stages.approvals,
        trackedEntityInstance: teiId,
        orgUnit: orgUnitId,
        enrollment: enrollmentId,
        eventDate: new Date().toISOString().split('T')[0],
        status: "COMPLETED",
        dataValues: dataValues,
      });
    }
  };

  // --- Get All Competency Configs (master list) ---
  const getCompetencyConfigs = async (orgUnitId) => {
    const events = await getEvents({
      program: CONFIG.program,
      programStage: CONFIG.stages.config,
      orgUnit: orgUnitId,
    });
    
    return events.map(event => {
      const dataValuesMap = {};
      event.dataValues?.forEach(dv => {
        dataValuesMap[dv.dataElement] = dv.value;
      });
      
      return {
        eventId: event.event,
        competencyId: dataValuesMap[CONFIG.de.competencyId],
        name: dataValuesMap[CONFIG.de.competencyCode] || "",
        definition: dataValuesMap[CONFIG.de.competencyDefinition] || "",
        weight: dataValuesMap[CONFIG.de.weightValue] ? parseFloat(dataValuesMap[CONFIG.de.weightValue]) : null,
        weightLocked: !!dataValuesMap[CONFIG.de.weightValue],
      };
    }).filter(c => c.name);
  };

  return {
    createEvent,
    getEvents,
    updateEvent,
    deleteEvent,
    createCompetencyConfig,
    updateCompetencyConfig,
    saveRating,
    saveApproval,
    getCompetencyConfigs,
    CONFIG
  };
}